import { groupSums, metricValue, type MediaMetric, type SpendImpressions } from './aggregate';
import { OTHER_DIMENSION, type DimensionRow, type MarketDimensionRow } from './dimensions';
import type { PathmaticsMonthlyRow } from './pathmaticsData.schema';

/**
 * A dimension carrying a fraction of a percent of budget can be many times the market average
 * while meaning nothing. Below this share an over-index is not worth reporting.
 */
export const MIN_OVER_INDEX_SHARE = 5;

/**
 * A brand tracking the market exactly still produces a maximum multiple, so without a floor
 * the panel presents "1.0x the market average" as an over-index. Media convention treats an
 * index of 120 as the point where a skew is real.
 */
export const MIN_OVER_INDEX_MULTIPLE = 1.2;

/**
 * Spend and impressions almost never move by identical percentages, so any gap at all would
 * fire the divergence clause. Below this many percentage points apart the two are tracking
 * each other and the clause would read a causal media story into rounding.
 */
export const MIN_DIVERGENCE_POINTS = 5;

/**
 * Below this magnitude a month-over-month move is noise the prose should not call a rise or a
 * fall, however large it is relative to the other months in range.
 */
export const FLAT_CHANGE_PERCENT = 0.05;

export interface TrendInsight {
  advertiser: string;
  month: string;
  /** Null when the range holds a single month, so there is no prior month to compare. */
  changePercent: number | null;
  value: number;
  metric: MediaMetric;
  divergence: 'efficiency' | 'costlier' | null;
  topDimension: string | null;
}

export interface OverIndexInsight {
  advertiser: string;
  dimension: string;
  sharePercent: number;
  marketSharePercent: number;
  multiple: number;
}

const EMPTY: SpendImpressions = { spend: 0, impressions: 0 };

function percentChange(previous: number, current: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

function divergenceFor(previous: SpendImpressions, current: SpendImpressions) {
  const spendChange = percentChange(previous.spend, current.spend);
  const impressionChange = percentChange(previous.impressions, current.impressions);
  if (spendChange === null || impressionChange === null) return null;
  // eslint-disable-next-line i18next/no-literal-string -- union discriminant, not copy
  if (impressionChange - spendChange >= MIN_DIVERGENCE_POINTS) return 'efficiency' as const;
  // eslint-disable-next-line i18next/no-literal-string -- union discriminant, not copy
  if (spendChange - impressionChange >= MIN_DIVERGENCE_POINTS) return 'costlier' as const;
  return null;
}

export function trendInsights(
  monthlyRows: PathmaticsMonthlyRow[],
  dimensionRows: DimensionRow[],
  advertisers: string[],
  months: string[],
  metric: MediaMetric,
): TrendInsight[] {
  const byAdvertiserMonth = groupSums(monthlyRows, (r) => `${r.advertiser}|${r.month}`);
  const dimensionsByAdvertiserMonth = groupSums(
    dimensionRows,
    (r) => `${r.advertiser}|${r.month}|${r.dimension}`,
  );

  const insights: TrendInsight[] = [];

  for (const advertiser of advertisers) {
    const totalsByMonth = months.map(
      (month) => byAdvertiserMonth.get(`${advertiser}|${month}`) ?? EMPTY,
    );
    const hasSpend = totalsByMonth.some((t) => t.spend > 0);
    if (!hasSpend) continue;

    let bestIndex = totalsByMonth.length - 1;
    let bestChange: number | null = null;
    for (let i = 1; i < totalsByMonth.length; i += 1) {
      const change = percentChange(
        metricValue(totalsByMonth[i - 1], metric),
        metricValue(totalsByMonth[i], metric),
      );
      if (change === null) continue;
      if (bestChange === null || Math.abs(change) > Math.abs(bestChange)) {
        bestChange = change;
        bestIndex = i;
      }
    }

    const month = months[bestIndex];
    const current = totalsByMonth[bestIndex];
    const previous = bestIndex > 0 ? totalsByMonth[bestIndex - 1] : null;

    const topDimension =
      Array.from(dimensionsByAdvertiserMonth.entries())
        .filter(([key]) => key.startsWith(`${advertiser}|${month}|`))
        .sort(([, a], [, b]) => b.spend - a.spend)
        .map(([key]) => key.split('|')[2])[0] ?? null;

    insights.push({
      advertiser,
      month,
      changePercent: bestChange,
      value: metricValue(current, metric),
      metric,
      divergence: previous ? divergenceFor(previous, current) : null,
      topDimension,
    });
  }

  return insights.sort((a, b) => Math.abs(b.changePercent ?? 0) - Math.abs(a.changePercent ?? 0));
}

/**
 * `renderedDimensions` are the columns the chart alongside this panel actually draws. Naming
 * anything else would point the reader at a dimension its legend folded into `Other`, so the
 * candidate set is restricted to them and `Other` itself is never a candidate: it is a bag of
 * dimensions, not one a brand can be said to over-index on.
 */
export function overIndexInsights(
  dimensionRows: DimensionRow[],
  marketRows: MarketDimensionRow[],
  advertisers: string[],
  renderedDimensions: string[],
): OverIndexInsight[] {
  const marketByDimension = groupSums(marketRows, (r) => r.dimension);
  const marketTotal = Array.from(marketByDimension.values()).reduce((sum, t) => sum + t.spend, 0);
  if (marketTotal === 0) return [];

  const candidates = new Set(renderedDimensions.filter((d) => d !== OTHER_DIMENSION));
  const insights: OverIndexInsight[] = [];

  for (const advertiser of advertisers) {
    const own = dimensionRows.filter((r) => r.advertiser === advertiser);
    const ownByDimension = groupSums(own, (r) => r.dimension);
    const ownTotal = Array.from(ownByDimension.values()).reduce((sum, t) => sum + t.spend, 0);
    if (ownTotal === 0) continue;

    let best: OverIndexInsight | null = null;
    for (const [dimension, totals] of ownByDimension) {
      if (!candidates.has(dimension)) continue;
      const sharePercent = (totals.spend / ownTotal) * 100;
      if (sharePercent < MIN_OVER_INDEX_SHARE) continue;
      const marketSpend = marketByDimension.get(dimension)?.spend ?? 0;
      const marketSharePercent = (marketSpend / marketTotal) * 100;
      if (marketSharePercent === 0) continue;
      const multiple = sharePercent / marketSharePercent;
      if (multiple < MIN_OVER_INDEX_MULTIPLE) continue;
      if (!best || multiple > best.multiple) {
        best = { advertiser, dimension, sharePercent, marketSharePercent, multiple };
      }
    }

    if (best) insights.push(best);
  }

  return insights.sort((a, b) => b.multiple - a.multiple);
}
